import React, { useState, useEffect } from 'react';
import { ScriptBreakdown as ScriptBreakdownType } from '../services/api';
import { StepCard } from './StepCard';
import { ViewModeToggle } from './ViewModeToggle';
import { ProcessFlowDiagramWrapper } from './ProcessFlowDiagram';
import { StepDetailsPanel } from './StepDetailsPanel';
import { ChevronRight, ChevronDown, Workflow, FileText, AlertTriangle, RefreshCw, Code, CheckCircle, XCircle } from 'lucide-react';
import { 
  getUserViewPreference,
  saveUserViewPreference 
} from '../utils/viewPreferences';
import './ScriptBreakdown.css';

interface ScriptBreakdownProps {
  breakdown: ScriptBreakdownType;
  scriptLines?: string[];
  isAdapted?: boolean;
  isStale?: boolean;
  onRegenerate?: () => void;
  scriptMetadata?: {
    scriptId: string;
    version: number;
    createdAt?: string;
    parentScriptId?: string;
  };
}

export const ScriptBreakdown: React.FC<ScriptBreakdownProps> = ({ 
  breakdown, 
  scriptLines, 
  isAdapted = false,
  isStale = false,
  onRegenerate,
  scriptMetadata
}) => {
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [selectedStep, setSelectedStep] = useState<string | null>(null);
  const [isMetadataExpanded, setIsMetadataExpanded] = useState(false);
  const [showStepDetails, setShowStepDetails] = useState(false);
  const [hasError, setHasError] = useState(false);
  
  // View mode state - always default to list unless user has a saved preference
  const [viewMode, setViewMode] = useState<'list' | 'flow'>(() => {
    const savedPreference = getUserViewPreference();
    return savedPreference || 'list';
  });
  
  // Validate breakdown structure on mount or when breakdown changes
  useEffect(() => {
    // Validate breakdown structure
    if (!breakdown || !breakdown.steps || !Array.isArray(breakdown.steps)) {
      setHasError(true);
      return;
    }
    
    setHasError(false);
  }, [breakdown]);
  
  const handleViewModeChange = (mode: 'list' | 'flow') => {
    setViewMode(mode);
    saveUserViewPreference(mode);
  };

  const toggleStep = (stepId: string) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  const handleStepClick = (stepId: string) => {
    setSelectedStep(stepId);
    toggleStep(stepId);
  };


  // Show error state if breakdown is invalid
  if (hasError) {
    return (
      <div className="breakdown-container">
        <div className="breakdown-error">
          <XCircle className="error-icon" size={24} />
          <h3>Unable to Display Process Flow</h3>
          <p>The process flow data is invalid or corrupted.</p>
          {onRegenerate && (
            <button className="regenerate-btn" onClick={onRegenerate}>
              <RefreshCw size={16} />
              Regenerate Process Flow
            </button>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="breakdown-container">
      {/* Show stale warning if breakdown is outdated */}
      {isStale && onRegenerate && (
        <div className="stale-warning-banner">
          <div className="stale-warning-content">
            <AlertTriangle className="warning-icon" size={20} />
            <div className="warning-text">
              <strong>Process Flow is Outdated</strong>
              <p>The script has been modified since this process flow was generated. The breakdown may not accurately reflect the current script behavior.</p>
            </div>
            <button className="regenerate-btn" onClick={onRegenerate}>
              <RefreshCw size={16} />
              Regenerate
            </button>
          </div>
        </div>
      )}
      
      {/* Summary Section */}
      <div className="breakdown-header">
        <div className="breakdown-header-top">
          <div className="breakdown-title-section">
            <Workflow className="breakdown-icon" />
            <h2 className="breakdown-title">Process Overview</h2>
            {scriptMetadata && (
              <>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginLeft: '12px',
                  padding: '3px 10px',
                  background: '#d4f4dd',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500
                }}>
                  <CheckCircle size={14} style={{ color: '#16a34a' }} />
                  <span style={{ color: '#15803d' }}>v{scriptMetadata.version}</span>
                </div>
                {scriptMetadata.createdAt && (
                  <span style={{
                    marginLeft: '8px',
                    fontSize: '11px',
                    color: '#6b7280',
                    fontWeight: 400
                  }}>
                    Generated: {new Date(scriptMetadata.createdAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </span>
                )}
              </>
            )}
            {isAdapted && (
              <span className="adapted-badge" style={{ marginLeft: '8px' }}>Adapted</span>
            )}
            {/* View Mode Toggle - Always shown */}
            <ViewModeToggle
              currentMode={viewMode}
              onModeChange={handleViewModeChange}
              disabled={false}
              showBetaBadge={false}
            />
            
            {scriptMetadata && onRegenerate && (
              <button
                onClick={onRegenerate}
                style={{
                  marginLeft: 'auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#374151',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
              >
                <RefreshCw size={12} />
                Regenerate
              </button>
            )}
          </div>
        </div>
        
        {/* Collapsible Script Metadata Section */}
        {scriptMetadata && (
          <div style={{
            background: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            marginBottom: '12px',
            overflow: 'hidden'
          }}>
            <div 
              onClick={() => setIsMetadataExpanded(!isMetadataExpanded)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                userSelect: 'none',
                fontSize: '12px',
                color: '#6c757d'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ 
                  transform: isMetadataExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  fontSize: '10px'
                }}>
                  ▶
                </span>
                <Code size={12} style={{ color: '#6c757d' }} />
                <span style={{ fontFamily: 'Monaco, Menlo, monospace' }}>
                  Script: {scriptMetadata.scriptId.slice(0, 8)}... • v{scriptMetadata.version}
                  {scriptMetadata.parentScriptId && ' • Revised'}
                </span>
              </div>
              <span style={{ fontSize: '11px', color: '#6c757d' }}>
                {isMetadataExpanded ? 'Hide Details' : 'Show Details'}
              </span>
            </div>
            
            {isMetadataExpanded && (
              <div style={{
                padding: '12px 16px',
                borderTop: '1px solid #dee2e6',
                fontSize: '13px',
                fontFamily: 'Monaco, Menlo, monospace',
                color: '#495057'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <strong style={{ color: '#6c757d', fontSize: '12px' }}>Full Script ID:</strong>
                    <div style={{ color: '#212529', fontSize: '12px', marginTop: '4px', wordBreak: 'break-all' }}>
                      {scriptMetadata.scriptId}
                    </div>
                  </div>
                  
                  {scriptMetadata.parentScriptId && (
                    <div>
                      <strong style={{ color: '#6c757d', fontSize: '12px' }}>Parent Script ID:</strong>
                      <div style={{ color: '#856404', fontSize: '12px', marginTop: '4px', wordBreak: 'break-all' }}>
                        {scriptMetadata.parentScriptId}
                      </div>
                    </div>
                  )}
                  
                  {scriptMetadata.createdAt && (
                    <div>
                      <strong style={{ color: '#6c757d', fontSize: '12px' }}>Generated:</strong>
                      <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                        {new Date(scriptMetadata.createdAt).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        <p className="breakdown-summary">{breakdown.summary}</p>
      </div>

      {/* Content based on view mode */}
      {viewMode === 'list' ? (
        /* List View - Current Implementation */
        <div className="breakdown-content">
          <div className="steps-list">
            {breakdown.steps.map((step, index) => (
            <div key={step.id} className="step-item">
              {/* Connection Line */}
              {index < breakdown.steps.length - 1 && (
                <div className="step-connection-line" />
              )}
              
              {/* Step Card */}
              <div
                className={`step-card ${selectedStep === step.id ? 'selected' : ''}`}
                onClick={() => handleStepClick(step.id)}
              >
                <div className="step-card-header">
                  {/* Step Number */}
                  <div className="step-number-circle">
                    <span>{index + 1}</span>
                  </div>
                  
                  {/* Step Content */}
                  <div className="step-content">
                    <div className="step-title-row">
                      <h3 className="step-title">{step.title}</h3>
                      <span className="step-type-badge">
                        {step.type}
                      </span>
                    </div>
                    <p className="step-description">{step.description}</p>
                    
                    {/* Code Reference */}
                    {scriptLines && (
                      <div className="step-code-ref">
                        <FileText />
                        <span>Lines {step.code_lines[0]}-{step.code_lines[1]}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Expand Icon */}
                  <div className="step-expand-icon">
                    {expandedSteps.has(step.id) ? (
                      <ChevronDown />
                    ) : (
                      <ChevronRight />
                    )}
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedSteps.has(step.id) && (
                  <div className="step-details-section">
                    <StepCard step={step} />
                  </div>
                )}
              </div>
            </div>
            ))}

            {/* Data Flow Section */}
            {breakdown.data_flow.length > 0 && (
              <div className="data-flow-section">
                <h3 className="data-flow-title">Data Flow</h3>
                <div className="data-flow-card">
                  <div className="data-flow-list">
                    {breakdown.data_flow.map((flow, index) => {
                      const fromIndex = breakdown.steps.findIndex(s => s.id === flow.from);
                      const toIndex = breakdown.steps.findIndex(s => s.id === flow.to);
                      
                      // Skip invalid flows
                      if (fromIndex === -1 || toIndex === -1) {
                        console.warn(`Invalid data flow reference: ${flow.from} -> ${flow.to}`);
                        return null;
                      }
                      
                      return (
                        <div key={index} className="data-flow-item">
                          <span className="data-flow-step">
                            Step {fromIndex + 1}
                          </span>
                          <span className="data-flow-arrow">→</span>
                          <span className="data-flow-step">
                            Step {toIndex + 1}
                          </span>
                          <span className="data-flow-description">: {flow.description}</span>
                        </div>
                      );
                    }).filter(Boolean)}
                  </div>
                </div>
              </div>
            )}

            {/* Accuracy Summary Section */}
            {breakdown.accuracy_summary && (
              <div className="accuracy-summary-section">
                <h3 className="accuracy-summary-title">Accuracy & Completeness</h3>
                <div className="accuracy-summary-card">
                  {breakdown.accuracy_summary.key_controls && (
                    <div className="accuracy-item">
                      <h4>Key Controls</h4>
                      <p>{breakdown.accuracy_summary.key_controls}</p>
                    </div>
                  )}
                  {breakdown.accuracy_summary.assumptions && (
                    <div className="accuracy-item">
                      <h4>Assumptions to Verify</h4>
                      <p>{breakdown.accuracy_summary.assumptions}</p>
                    </div>
                  )}
                  {breakdown.accuracy_summary.completeness_checks && (
                    <div className="accuracy-item">
                      <h4>Completeness Checks</h4>
                      <p>{breakdown.accuracy_summary.completeness_checks}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Flow Diagram View */
        <div className="breakdown-content flow-view" style={{ position: 'relative' }}>
          <ProcessFlowDiagramWrapper
            breakdown={breakdown}
            onStepClick={(stepId) => {
              setSelectedStep(stepId);
              setShowStepDetails(true);
            }}
            highlightedStep={selectedStep}
          />
          {showStepDetails && selectedStep && (
            <StepDetailsPanel
              step={breakdown.steps.find(s => s.id === selectedStep) || null}
              stepNumber={breakdown.steps.findIndex(s => s.id === selectedStep) + 1}
              onClose={() => {
                setShowStepDetails(false);
                setSelectedStep(null);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};